import { useEffect, useState } from 'react';

// True once the browser has had a chance to paint the first frame of the
// current route. Below-the-fold sections and floating widgets that are
// code-split wait for this before mounting, so their chunk requests start
// after first paint instead of racing it. (When they start at mount time,
// Lighthouse's throttled simulation treats all ~30 of those requests as
// render-critical and reports a first/largest contentful paint far later than
// the real one; on a slow connection they also compete with the hero for
// bandwidth.) Content still renders within a frame or two, so crawlers that
// wait for network idle see the full page.
export default function useAfterFirstPaint() {
  const [afterPaint, setAfterPaint] = useState(false);
  useEffect(() => {
    let done = false;
    let frame;
    let timer;
    let idle;
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'];

    const fire = () => {
      if (done) return;
      done = true;
      events.forEach(name => window.removeEventListener(name, fire));
      if (idle !== undefined && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idle);
      clearTimeout(timer);
      setAfterPaint(true);
    };

    const scheduleIdle = () => {
      if (typeof window.requestIdleCallback === 'function') idle = window.requestIdleCallback(fire, { timeout: 900 });
      else timer = setTimeout(fire, 600);
    };

    // One painted frame first, then wait for the main thread to go idle (or the
    // first sign of intent) so the hero is interactive before eight more
    // sections and their chunks are mounted. Phones otherwise spend the whole
    // first second rendering below-the-fold content nobody has scrolled to.
    frame = requestAnimationFrame(() => { timer = setTimeout(scheduleIdle, 0); });
    events.forEach(name => window.addEventListener(name, fire, { passive: true }));

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      if (idle !== undefined && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idle);
      events.forEach(name => window.removeEventListener(name, fire));
    };
  }, []);
  return afterPaint;
}